import type { ReactElement } from 'react';
import type { MemorialData, Offering, ProductItem, MediaItem } from '../hooks/useMemorialData';
import PropBar from '../components/PropBar';
import { ProductIcon } from '../lib/svg';
import { Icon } from '../lib/icons';

export interface TemplateProps {
  memorial: MemorialData;
  offerings: Offering[];
  media: MediaItem[];
  groups: { categoryId: string; items: ProductItem[] }[];
  activeCat: string;
  onSelectCat: (id: string) => void;
  onPick: (p: ProductItem) => void;
}

const EFFECT_CLASS: Record<string, string> = {
  none: '',
  glow: 'animate-breathe',
  float: 'animate-float',
  particle: 'animate-float',
  beam: 'animate-breathe',
  ripple: 'animate-float',
};

function OfferingIcon({ o }: { o: Offering }) {
  const paid = o.product.tier === 'paid';
  return (
    <div className={`text-center ${paid ? 'scale-105' : ''}`}>
      <div
        className={`mx-auto flex h-12 w-12 items-center justify-center rounded-xl text-2xl ${
          paid ? 'bg-[var(--c-accent-soft)] ring-1 ring-[var(--c-accent)]/40' : 'bg-[var(--c-bg)]'
        } ${EFFECT_CLASS[o.product.effectType] ?? ''}`}
      >
        <ProductIcon iconSvg={o.product.iconSvg} size={32} />
      </div>
      <p
        className={`mt-1.5 truncate text-[10px] ${paid ? 'text-[var(--c-accent)]' : 'text-[var(--c-muted)]'}`}
      >
        {o.fromName}
      </p>
    </div>
  );
}

/** 遗像来源：优先用馆主上传的头像，否则回落到相册首张照片 */
function portraitOf(memorial: MemorialData, media: MediaItem[]): string | null {
  if (memorial.portraitUrl) return memorial.portraitUrl;
  return media.find((m) => m.type === 'photo')?.url ?? null;
}

type MemorialPerson = NonNullable<MemorialData['people']>[number];

/**
 * 合葬区块：并排展示两位逝者，各自带遗像、姓名与生卒。
 *
 * 各模板版式差异很大（竖排、灵位牌、挽联、花圈……），统一在「姓名+生卒」
 * 处改用本区块，既保留各自整体构图，又能完整呈现两位逝者的独立信息。
 */
export function CoupleBlock({ people }: { people: MemorialPerson[] }) {
  return (
    <div className="flex flex-wrap items-stretch justify-center gap-4">
      {people.map((p) => (
        <div
          key={p.id}
          className="w-40 rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)] p-4 text-center"
        >
          <div className="mx-auto h-20 w-20 overflow-hidden rounded-full border border-[var(--c-border)] bg-[var(--c-bg)]">
            {p.portraitUrl ? (
              <img src={p.portraitUrl} alt={p.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-[var(--c-muted)]">
                <Icon name="person" size={30} />
              </div>
            )}
          </div>
          <p className="mt-2.5 font-serifcn text-lg text-[var(--c-text)]">{p.name}</p>
          <p className="mt-1 text-xs text-[var(--c-muted)]">
            {p.birthDate ?? '—'} — {p.deathDate ?? '—'}
          </p>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// 1. 经典追思 —— 居中对称，庄重耐看（原始样式）
// ============================================================
export function ClassicTemplate({
  memorial,
  offerings,
  media,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  // 双人合葬：people 会有 2 位（sortOrder 0 为主逝者）
  const people = memorial.people ?? [];
  return (
    <div>
      <div className="mt-10 text-center">
        <p className="text-xs tracking-[0.3em] text-[var(--c-muted)]">IN LOVING MEMORY</p>
        <h1 className="mt-4 font-serifcn text-4xl text-[var(--c-text)]">
          {memorial.deceasedName}
        </h1>
        {memorial.deceasedNameEn && (
          <p className="mt-1 text-sm italic text-[var(--c-muted)]">{memorial.deceasedNameEn}</p>
        )}
        {people.length > 1 ? (
          // 合葬：并排展示两位，各自带遗像与生卒
          <div className="mt-5">
            <CoupleBlock people={people} />
          </div>
        ) : (
          (memorial.birthDate || memorial.deathDate) && (
            <p className="mt-4 text-sm text-[var(--c-muted)]">
              {memorial.birthDate ?? '—'} — {memorial.deathDate ?? '—'}
            </p>
          )
        )}
        {memorial.epitaph && (
          <p className="mx-auto mt-6 max-w-lg font-serifcn text-base leading-relaxed text-[var(--c-text)]">
            「{memorial.epitaph}」
          </p>
        )}
        {memorial.biography && (
          <p className="mx-auto mt-4 max-w-2xl whitespace-pre-wrap text-sm leading-relaxed text-[var(--c-muted)]">
            {memorial.biography}
          </p>
        )}
        <p className="mt-6 text-xs text-[var(--c-muted)]">
          已被祭奠 {memorial.tributeCount} 次 · 浏览 {memorial.viewCount}
        </p>
      </div>

      <section className="mt-14">
        <h2 className="mb-5 text-center text-xs tracking-[0.2em] text-[var(--c-muted)]">
          祭 台
        </h2>
        <OfferingGrid offerings={offerings} />
      </section>

      <BottomPad />
      <PropBarSlot
        groups={groups}
        activeCat={activeCat}
        onSelectCat={onSelectCat}
        onPick={onPick}
      />
    </div>
  );
}

// ============================================================
// 2. 中式古典 —— 卷轴竖排、中堂香炉、印章纹样
// ============================================================
export function ChineseTemplate({
  memorial,
  offerings,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  return (
    <div className="relative">
      {/* 卷轴底纹 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-64 opacity-[0.07]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, var(--c-text) 0 1px, transparent 1px 28px)',
        }}
      />

      <div className="relative mx-auto max-w-3xl px-4 pt-12">
        {/* 竖排姓名 + 印章 */}
        <div className="flex items-start justify-center gap-8">
          <div
            className="font-serifcn text-5xl leading-tight tracking-[0.2em] text-[var(--c-text)]"
            style={{ writingMode: 'vertical-rl' }}
          >
            {memorial.deceasedName}
          </div>

          <div className="flex flex-col items-center gap-6 pt-4">
            <span className="rounded-sm bg-[var(--c-accent)] px-2 py-6 text-xs leading-tight text-white"
              style={{ writingMode: 'vertical-rl', letterSpacing: '0.2em' }}>
              永垂不朽
            </span>
          </div>

          {(memorial.people ?? []).length > 1 ? (
            <div className="flex flex-col justify-center pt-2">
              <CoupleBlock people={memorial.people ?? []} />
            </div>
          ) : (
            <div className="flex flex-col justify-center pt-2">
              <p className="text-sm text-[var(--c-muted)]">
                生於 {memorial.birthDate ?? '不詳'}
              </p>
              <p className="mt-1 text-sm text-[var(--c-muted)]">
                卒於 {memorial.deathDate ?? '不詳'}
              </p>
            </div>
          )}
        </div>

        {/* 对联式墓志铭 */}
        {memorial.epitaph && (
          <div className="mt-10 flex justify-center gap-6">
            <p className="max-w-[40%] border-r-2 border-[var(--c-accent)]/30 pr-4 text-right font-serifcn text-base leading-relaxed text-[var(--c-text)]">
              {memorial.epitaph.slice(0, Math.ceil(memorial.epitaph.length / 2))}
            </p>
            <p className="max-w-[40%] border-l-2 border-[var(--c-accent)]/30 pl-4 font-serifcn text-base leading-relaxed text-[var(--c-text)]">
              {memorial.epitaph.slice(Math.ceil(memorial.epitaph.length / 2))}
            </p>
          </div>
        )}

        {/* 中堂：香炉 */}
        <section className="mt-12">
          <div className="relative mx-auto max-w-md rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-6 py-10 text-center">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--c-bg)] px-3 font-serifcn text-xs text-[var(--c-muted)]">
              中堂供奉
            </div>

            {offerings.length === 0 ? (
              <div className="py-8">
                <div className="flex justify-center"><ProductIcon size={44} /></div>
                <p className="mt-3 text-xs text-[var(--c-muted)]">尚無供品，敬請上香</p>
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-3">
                {offerings.slice(0, 15).map((o) => (
                  <div key={o.id} className="text-center">
                    <div
                      className={`mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--c-accent-soft)] text-xl ${
                        EFFECT_CLASS[o.product.effectType] ?? ''
                      }`}
                    >
                      <ProductIcon iconSvg={o.product.iconSvg} size={28} />
                    </div>
                    <p className="mt-1 truncate text-[9px] text-[var(--c-muted)]">
                      {o.fromName}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <p className="mt-6 text-xs text-[var(--c-muted)]">
              共 {memorial.tributeCount} 次祭奠
            </p>
          </div>
        </section>

        {memorial.biography && (
          <div className="mt-10 rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-6">
            <p className="mb-3 font-serifcn text-sm text-[var(--c-text)]">生平事略</p>
            <p className="whitespace-pre-wrap text-sm leading-loose text-[var(--c-muted)]">
              {memorial.biography}
            </p>
          </div>
        )}
      </div>

      <BottomPad />
      <PropBarSlot
        groups={groups}
        activeCat={activeCat}
        onSelectCat={onSelectCat}
        onPick={onPick}
      />
    </div>
  );
}

// ============================================================
// 3. 暗色守夜 —— 全屏暗调、烛光呼吸、守灵氛围
// ============================================================
export function VigilTemplate({
  memorial,
  offerings,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  return (
    <div className="relative min-h-screen">
      {/* 烛光辉光 */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-24 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-[var(--c-accent)]/20 blur-3xl animate-breathe"
      />

      <div className="relative mx-auto max-w-2xl px-6 pt-20 text-center">
        <div className="flex justify-center"><ProductIcon size={56} /></div>
        <h1 className="mt-6 font-serifcn text-4xl text-[var(--c-text)]">
          {memorial.deceasedName}
        </h1>

        {(memorial.people ?? []).length > 1 ? (
          <div className="mt-4">
            <CoupleBlock people={memorial.people ?? []} />
          </div>
        ) : (
          (memorial.birthDate || memorial.deathDate) && (
            <p className="mt-3 text-sm text-[var(--c-muted)]">
              {memorial.birthDate ?? '—'} — {memorial.deathDate ?? '—'}
            </p>
          )
        )}

        {memorial.epitaph && (
          <p className="mx-auto mt-8 max-w-md font-serifcn text-lg leading-relaxed text-[var(--c-accent)]">
            「{memorial.epitaph}」
          </p>
        )}

        {memorial.biography && (
          <p className="mx-auto mt-6 max-w-lg whitespace-pre-wrap text-sm leading-loose text-[var(--c-muted)]">
            {memorial.biography}
          </p>
        )}

        {/* 烛火环绕祭台 */}
        <section className="mt-16">
          <div className="flex flex-wrap justify-center gap-5">
            {offerings.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-sm text-[var(--c-muted)]">還沒有人前來守夜</p>
                <p className="mt-1 text-xs text-[var(--c-muted)]">點一盞燈，陪他一會兒</p>
              </div>
            ) : (
              offerings.slice(0, 24).map((o) => (
                <div key={o.id} className="group text-center">
                  <div
                    className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--c-accent)]/10 text-2xl ring-1 ring-[var(--c-accent)]/25 ${
                      EFFECT_CLASS[o.product.effectType] ?? ''
                    }`}
                  >
                    <ProductIcon iconSvg={o.product.iconSvg} size={36} />
                  </div>
                  <p className="mt-2 max-w-[64px] truncate text-[10px] text-[var(--c-muted)]">
                    {o.fromName}
                  </p>
                </div>
              ))
            )}
          </div>

          <p className="mt-10 text-xs text-[var(--c-muted)]">
            {memorial.tributeCount} 盞燈火為他亮著
          </p>
        </section>
      </div>

      <BottomPad />
      <PropBarSlot
        groups={groups}
        activeCat={activeCat}
        onSelectCat={onSelectCat}
        onPick={onPick}
      />
    </div>
  );
}

// ============================================================
// 4. 现代卡片 —— 大图头图、时间轴生平、卡片墙
// ============================================================
export function CardsTemplate({
  memorial,
  offerings,
  media,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  const cover = portraitOf(memorial, media);
  return (
    <div className="mx-auto max-w-4xl">
      {/* 头图：有照片用照片，否则用渐变色块 */}
      <div className="mt-8 h-52 overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--c-accent-soft)] to-[var(--c-bg-grad)] sm:h-64">
        {cover ? (
          <img src={cover} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center">
            <ProductIcon iconSvg={offerings[0]?.product?.iconSvg} size={56} />
          </div>
        )}
      </div>

      {/* 姓名卡 */}
      <div className="-mt-10 mx-4 rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)] p-6 shadow-sm">
        <h1 className="font-serifcn text-3xl text-[var(--c-text)]">
          {memorial.deceasedName}
        </h1>
        {(memorial.people ?? []).length > 1 ? (
          <div className="mt-3">
            <CoupleBlock people={memorial.people ?? []} />
          </div>
        ) : (
          <p className="mt-2 text-sm text-[var(--c-muted)]">
            {memorial.birthDate ?? '—'} — {memorial.deathDate ?? '—'}
          </p>
        )}
        {memorial.epitaph && (
          <p className="mt-4 font-serifcn text-base text-[var(--c-text)]">
            「{memorial.epitaph}」
          </p>
        )}
        <div className="mt-5 flex gap-5 border-t border-[var(--c-border)] pt-4 text-xs text-[var(--c-muted)]">
          <span>祭品 {memorial.tributeCount}</span>
          <span>浏览 {memorial.viewCount}</span>
        </div>
      </div>

      <div className="grid gap-6 px-4 lg:grid-cols-2">
        {/* 生平时间轴 */}
        {memorial.biography && (
          <section className="mt-8">
            <h2 className="mb-4 text-xs tracking-[0.2em] text-[var(--c-muted)]">生平</h2>
            <div className="relative border-l-2 border-[var(--c-border)] pl-5">
              <div className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-[var(--c-accent)]" />
              <p className="whitespace-pre-wrap text-sm leading-loose text-[var(--c-muted)]">
                {memorial.biography}
              </p>
            </div>
          </section>
        )}

        {/* 追思卡片墙 */}
        <section className="mt-8">
          <h2 className="mb-4 text-xs tracking-[0.2em] text-[var(--c-muted)]">追思</h2>
          {offerings.length === 0 ? (
            <p className="text-sm text-[var(--c-muted)]">还没有人留下祭品</p>
          ) : (
            <div className="space-y-2">
              {offerings.slice(0, 12).map((o) => (
                <div
                  key={o.id}
                  className="flex items-start gap-3 rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-3"
                >
                  <ProductIcon iconSvg={o.product.iconSvg} size={20} />
                  <div className="min-w-0">
                    <p className="text-xs text-[var(--c-muted)]">{o.fromName}</p>
                    <p className="mt-0.5 truncate text-sm text-[var(--c-text)]">
                      {o.dedication ?? o.product.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <BottomPad />
      <PropBarSlot
        groups={groups}
        activeCat={activeCat}
        onSelectCat={onSelectCat}
        onPick={onPick}
      />
    </div>
  );
}

// ============================================================
// 5. 简洁极简 —— 大量留白，文字为主，无装饰
// ============================================================
export function MinimalTemplate({
  memorial,
  offerings,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  return (
    <div className="mx-auto max-w-xl px-8">
      <div className="pt-28">
        <h1 className="font-serifcn text-3xl font-light leading-snug text-[var(--c-text)]">
          {memorial.deceasedName}
        </h1>

        {(memorial.people ?? []).length > 1 ? (
          <div className="mt-4">
            <CoupleBlock people={memorial.people ?? []} />
          </div>
        ) : (
          (memorial.birthDate || memorial.deathDate) && (
            <p className="mt-3 text-sm font-light text-[var(--c-muted)]">
              {memorial.birthDate ?? '—'} — {memorial.deathDate ?? '—'}
            </p>
          )
        )}

        {memorial.epitaph && (
          <p className="mt-10 font-serifcn text-lg font-light leading-loose text-[var(--c-text)]">
            {memorial.epitaph}
          </p>
        )}

        {memorial.biography && (
          <p className="mt-8 whitespace-pre-wrap text-sm font-light leading-loose text-[var(--c-muted)]">
            {memorial.biography}
          </p>
        )}

        <div className="mt-16 flex flex-wrap items-center gap-x-4 gap-y-3">
          {offerings.length === 0 ? (
            <p className="text-sm font-light text-[var(--c-muted)]">尚无祭品</p>
          ) : (
            offerings.slice(0, 30).map((o) => (
              <span
                key={o.id}
                className={`text-lg ${EFFECT_CLASS[o.product.effectType] ?? ''}`}
                title={`${o.fromName}：${o.dedication ?? o.product.name}`}
              >
                <ProductIcon iconSvg={o.product.iconSvg} size={18} />
              </span>
            ))
          )}
        </div>

        <p className="mt-10 text-xs font-light text-[var(--c-muted)]">
          {memorial.tributeCount} 次祭奠
        </p>
      </div>

      <BottomPad />
      <PropBarSlot
        groups={groups}
        activeCat={activeCat}
        onSelectCat={onSelectCat}
        onPick={onPick}
      />
    </div>
  );
}

// ============================================================
// 6. 灵堂·中堂 —— 灵位牌 + 遗像黑框 + 左右挽联 + 香案供奉
// ============================================================
export function LingtangHallTemplate({
  memorial,
  offerings,
  media,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  const portrait = portraitOf(memorial, media);
  return (
    <div className="relative">
      {/* 卷轴底纹 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-72 opacity-[0.06]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, var(--c-text) 0 1px, transparent 1px 28px)',
        }}
      />

      <div className="relative mx-auto max-w-3xl px-4 pt-10">
        {/* 灵位牌 */}
        <div className="mx-auto w-fit rounded-md border border-[var(--c-border)] bg-[var(--c-text)] px-5 py-3 text-center shadow">
          <p className="font-serifcn text-lg tracking-[0.3em] text-[var(--c-bg)]">
            {memorial.deceasedName} 之靈位
          </p>
        </div>

        {/* 遗像框 + 左右挽联 */}
        <div className="mt-8 flex items-stretch justify-center gap-4">
          <div className="hidden w-12 flex-col items-center justify-center gap-2 sm:flex">
            <p
              className="font-serifcn text-sm leading-loose tracking-[0.2em] text-[var(--c-muted)]"
              style={{ writingMode: 'vertical-rl' }}
            >
              {memorial.epitaph
                ? memorial.epitaph.slice(0, Math.ceil(memorial.epitaph.length / 2))
                : '音容宛在'}
            </p>
          </div>

          {(memorial.people ?? []).length > 1 ? (
            // 合葬：并排两副遗像，各自标注姓名
            (memorial.people ?? []).map((p) => (
              <div
                key={p.id}
                className="relative w-44 rounded-lg border-4 border-[var(--c-text)] bg-[var(--c-surface)] p-1.5 shadow-lg"
              >
                <div className="aspect-square overflow-hidden rounded bg-[var(--c-bg)]">
                  {p.portraitUrl ? (
                    <img src={p.portraitUrl} alt={p.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-[var(--c-muted)]"><Icon name="person" size={64} /></div>
                  )}
                </div>
                <span className="absolute -left-2 top-2 h-10 w-3 -rotate-12 bg-black/60" />
                <span className="absolute -right-2 top-2 h-10 w-3 rotate-12 bg-black/60" />
                <p className="mt-2 text-center font-serifcn text-sm text-[var(--c-text)]">{p.name}</p>
              </div>
            ))
          ) : (
            <div className="relative w-44 rounded-lg border-4 border-[var(--c-text)] bg-[var(--c-surface)] p-1.5 shadow-lg">
              <div className="aspect-square overflow-hidden rounded bg-[var(--c-bg)]">
                {portrait ? (
                  <img src={portrait} alt={memorial.deceasedName} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-[var(--c-muted)]"><Icon name="person" size={64} /></div>
                )}
              </div>
              <span className="absolute -left-2 top-2 h-10 w-3 -rotate-12 bg-black/60" />
              <span className="absolute -right-2 top-2 h-10 w-3 rotate-12 bg-black/60" />
            </div>
          )}

          <div className="hidden w-12 flex-col items-center justify-center gap-2 sm:flex">
            <p
              className="font-serifcn text-sm leading-loose tracking-[0.2em] text-[var(--c-muted)]"
              style={{ writingMode: 'vertical-rl' }}
            >
              {memorial.epitaph
                ? memorial.epitaph.slice(Math.ceil(memorial.epitaph.length / 2))
                : '德泽长存'}
            </p>
          </div>
        </div>

        <p className="mt-5 text-center text-sm text-[var(--c-muted)]">
          {memorial.birthDate ?? '—'} — {memorial.deathDate ?? '—'}
        </p>

        {/* 香案 */}
        <section className="mt-8">
          <div className="relative mx-auto max-w-md rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-6 py-8 text-center">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--c-bg)] px-3 font-serifcn text-xs text-[var(--c-muted)]">
              香 案
            </div>
            <ProductIcon size={36} />
            <div className="mt-4">
              {offerings.length === 0 ? (
                <p className="text-xs text-[var(--c-muted)]">敬备清供，恭候亲邻致祭</p>
              ) : (
                <div className="grid grid-cols-5 gap-3">
                  {offerings.slice(0, 15).map((o) => (
                    <div key={o.id} className="text-center">
                      <div
                        className={`mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--c-accent-soft)] text-xl ${
                          EFFECT_CLASS[o.product.effectType] ?? ''
                        }`}
                      >
                        <ProductIcon iconSvg={o.product.iconSvg} size={28} />
                      </div>
                      <p className="mt-1 truncate text-[9px] text-[var(--c-muted)]">
                        {o.fromName}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-5 text-xs text-[var(--c-muted)]">共 {memorial.tributeCount} 次祭奠</p>
          </div>
        </section>

        {memorial.biography && (
          <div className="mt-8 rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-6">
            <p className="mb-3 font-serifcn text-sm text-[var(--c-text)]">生平事略</p>
            <p className="whitespace-pre-wrap text-sm leading-loose text-[var(--c-muted)]">
              {memorial.biography}
            </p>
          </div>
        )}
      </div>

      <BottomPad />
      <PropBarSlot groups={groups} activeCat={activeCat} onSelectCat={onSelectCat} onPick={onPick} />
    </div>
  );
}

// ============================================================
// 7. 灵堂·守夜 —— 遗像居于烛光辉光中，灯火环绕陪灵
// ============================================================
export function LingtangNightTemplate({
  memorial,
  offerings,
  media,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  const portrait = portraitOf(memorial, media);
  return (
    <div className="relative min-h-screen">
      {/* 烛光辉光 */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-20 h-[460px] w-[460px] -translate-x-1/2 rounded-full bg-[var(--c-accent)]/15 blur-3xl animate-breathe"
      />

      <div className="relative mx-auto max-w-2xl px-6 pt-16 text-center">
        <p className="text-xs tracking-[0.4em] text-[var(--c-muted)]">守 靈 廳</p>

        {/* 遗像（圆形 + 黑纱） */}
        <div className="relative mx-auto mt-6 h-40 w-40">
          <div className="h-full w-full overflow-hidden rounded-full border-4 border-[var(--c-text)] bg-[var(--c-surface)] shadow-xl">
            {portrait ? (
              <img src={portrait} alt={memorial.deceasedName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-[var(--c-muted)]"><Icon name="person" size={64} /></div>
            )}
          </div>
          <span className="absolute -left-3 top-3 h-12 w-3 -rotate-12 bg-black/60" />
          <span className="absolute -right-3 top-3 h-12 w-3 rotate-12 bg-black/60" />
        </div>

        <h1 className="mt-6 font-serifcn text-4xl text-[var(--c-text)]">{memorial.deceasedName}</h1>
        {(memorial.people ?? []).length > 1 ? (
          <div className="mt-3">
            <CoupleBlock people={memorial.people ?? []} />
          </div>
        ) : (
          <p className="mt-2 text-sm text-[var(--c-muted)]">
            {memorial.birthDate ?? '—'} — {memorial.deathDate ?? '—'}
          </p>
        )}

        {memorial.epitaph && (
          <p className="mx-auto mt-6 max-w-md font-serifcn text-lg leading-relaxed text-[var(--c-accent)]">
            「{memorial.epitaph}」
          </p>
        )}

        {/* 烛火环绕祭台 */}
        <section className="mt-12">
          <div className="flex flex-wrap justify-center gap-5">
            {offerings.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-[var(--c-muted)]">還沒有人前來守夜</p>
                <p className="mt-1 text-xs text-[var(--c-muted)]">點一盞燈，陪他一會兒</p>
              </div>
            ) : (
              offerings.slice(0, 24).map((o) => (
                <div key={o.id} className="text-center">
                  <div
                    className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--c-accent)]/10 text-2xl ring-1 ring-[var(--c-accent)]/25 ${
                      EFFECT_CLASS[o.product.effectType] ?? ''
                    }`}
                  >
                    <ProductIcon iconSvg={o.product.iconSvg} size={36} />
                  </div>
                  <p className="mt-2 max-w-[64px] truncate text-[10px] text-[var(--c-muted)]">
                    {o.fromName}
                  </p>
                </div>
              ))
            )}
          </div>
          <p className="mt-8 text-xs text-[var(--c-muted)]">{memorial.tributeCount} 盞燈火為他亮著</p>
        </section>

        {memorial.biography && (
          <p className="mx-auto mt-10 max-w-lg whitespace-pre-wrap text-sm leading-loose text-[var(--c-muted)]">
            {memorial.biography}
          </p>
        )}
      </div>

      <BottomPad />
      <PropBarSlot groups={groups} activeCat={activeCat} onSelectCat={onSelectCat} onPick={onPick} />
    </div>
  );
}

// ============================================================
// 8. 灵堂·花祭 —— 遗像居于花圈中央，四周敬献鲜花祭品
// ============================================================
export function LingtangWreathTemplate({
  memorial,
  offerings,
  media,
  groups,
  activeCat,
  onSelectCat,
  onPick,
}: TemplateProps) {
  const portrait = portraitOf(memorial, media);
  return (
    <div className="relative mx-auto max-w-3xl px-4 pt-12">
      <p className="text-center text-xs tracking-[0.4em] text-[var(--c-muted)]">花 祭 靈 堂</p>

      {/* 花圈 + 遗像 */}
      <div className="relative mx-auto mt-8 h-64 w-64">
        <div className="absolute inset-0 rounded-full border-[10px] border-[var(--c-accent-soft)]" />
        <div className="absolute inset-2 rounded-full border border-[var(--c-border)]" />
        {[0, 90, 180, 270].map((deg) => (
          <span
            key={deg}
            className="absolute left-1/2 top-1/2 text-2xl"
            style={{ transform: `rotate(${deg}deg) translateY(-118px)` }}
          >
            <Icon name="flower" size={28} className="text-[var(--c-accent)]" />
          </span>
        ))}
        <div className="absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full border-4 border-[var(--c-surface)] bg-[var(--c-surface)] shadow-lg">
          {portrait ? (
            <img src={portrait} alt={memorial.deceasedName} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-[var(--c-muted)]"><Icon name="person" size={64} /></div>
          )}
        </div>
      </div>

      <h1 className="mt-6 text-center font-serifcn text-4xl text-[var(--c-text)]">
        {memorial.deceasedName}
      </h1>
      {(memorial.people ?? []).length > 1 ? (
        <div className="mt-3">
          <CoupleBlock people={memorial.people ?? []} />
        </div>
      ) : (
        <p className="mt-2 text-center text-sm text-[var(--c-muted)]">
          {memorial.birthDate ?? '—'} — {memorial.deathDate ?? '—'}
        </p>
      )}

      {memorial.epitaph && (
        <p className="mx-auto mt-6 max-w-lg text-center font-serifcn text-lg leading-relaxed text-[var(--c-text)]">
          「{memorial.epitaph}」
        </p>
      )}

      <section className="mt-10">
        <h2 className="mb-4 text-center text-xs tracking-[0.2em] text-[var(--c-muted)]">敬 献</h2>
        <OfferingGrid offerings={offerings} />
      </section>

      {memorial.biography && (
        <div className="mt-10 rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-6">
          <p className="mb-3 font-serifcn text-sm text-[var(--c-text)]">生平事略</p>
          <p className="whitespace-pre-wrap text-sm leading-loose text-[var(--c-muted)]">
            {memorial.biography}
          </p>
        </div>
      )}

      <BottomPad />
      <PropBarSlot groups={groups} activeCat={activeCat} onSelectCat={onSelectCat} onPick={onPick} />
    </div>
  );
}

// ============================================================
// 模板注册表
// ============================================================
export const TEMPLATE_MAP: Record<string, (p: TemplateProps) => ReactElement> = {
  classic: ClassicTemplate,
  chinese: ChineseTemplate,
  vigil: VigilTemplate,
  cards: CardsTemplate,
  minimal: MinimalTemplate,
  'lingtang-hall': LingtangHallTemplate,
  'lingtang-night': LingtangNightTemplate,
  'lingtang-wreath': LingtangWreathTemplate,
};

export function OfferingGrid({ offerings }: { offerings: Offering[] }) {
  if (offerings.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--c-border)] py-14 text-center">
        <ProductIcon size={36} />
        <p className="mt-3 text-sm text-[var(--c-muted)]">还没有人敬献祭品</p>
        <p className="mt-1 text-xs text-[var(--c-muted)]">点击下方道具，送出第一份思念</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 lg:grid-cols-8">
      {offerings.map((o) => (
        <OfferingIcon key={o.id} o={o} />
      ))}
    </div>
  );
}

/** 底部留白，避免内容被固定道具栏遮挡 */
function BottomPad() {
  return <div className="h-40" />;
}

function PropBarSlot(props: {
  groups: { categoryId: string; items: ProductItem[] }[];
  activeCat: string;
  onSelectCat: (id: string) => void;
  onPick: (p: ProductItem) => void;
}) {
  return <PropBar {...props} />;
}
